import { useCallback, useMemo } from "react";
import { DATA_VAULT_CONTEXT } from "../../constants";
import {
  useGetDataVaultExtensionFieldSectionsByContextQuery,
  useCreateDataVaultExtensionFieldSectionMutation,
  useUpdateDataVaultExtensionFieldSectionMutation,
  ExtensionFieldObjectType,
} from "../graphql";
import {
  Section,
  CreateSectionInput,
  UpdateSectionInput,
  CustomField,
  SectionWithFields,
  TabWithSections,
} from "../types";
import { useCustomFields } from "./useCustomFields";

interface UseSectionsResult {
  sections: Section[] | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  createSection: (input: CreateSectionInput) => Promise<Section>;
  updateSection: (input: UpdateSectionInput) => Promise<Section>;
  sectionsWithFields: SectionWithFields[];
  tabsWithSections: TabWithSections[];
  extensionFields: CustomField[];
  rawExtensionFields: ExtensionFieldObjectType[];
}

export const useSections = (context: string = DATA_VAULT_CONTEXT): UseSectionsResult => {
  const {
    data: customFields,
    loading: customFieldsLoading,
    rawExtensionFields,
  } = useCustomFields();
  const [createSectionMutation] = useCreateDataVaultExtensionFieldSectionMutation();
  const [updateSectionMutation] = useUpdateDataVaultExtensionFieldSectionMutation();

  // Query for sections
  const {
    data: sectionsQueryData,
    loading,
    error,
    refetch,
  } = useGetDataVaultExtensionFieldSectionsByContextQuery({
    variables: {
      input: {
        context,
        includeDeleted: false,
      },
    },

    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
    skip: !context,
  });

  // Transform backend data to frontend Section format
  const sections = useMemo(() => {
    if (!sectionsQueryData?.dataVaultExtensionFieldSectionsByContext) return null;

    return sectionsQueryData.dataVaultExtensionFieldSectionsByContext
      .filter((section): section is NonNullable<typeof section> => section !== null)
      .map((section): Section => ({
        id: section.id,
        name: section.name,
        description: section.description || undefined,
        context: section.context,
        sortOrder: section.sortOrder,
        extensionFieldIds: section.extensionFields?.map((field) => field.id) || [],
        fieldCount: section.fieldCount || 0,
        tabName: section.tabName || undefined,
        createdAt: section.createdAt,
        modifiedAt: section.modifiedAt || undefined,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [sectionsQueryData]);

  const createSection = useCallback(
    async (input: CreateSectionInput): Promise<Section> => {
      try {
        const result = await createSectionMutation({
          variables: {
            input: {
              name: input.name.trim(),
              description: input.description?.trim() || undefined,
              context: input.context,
              tabName: input.tabName?.trim() || undefined,
            },
          },

          refetchQueries: ["GetDataVaultExtensionFieldSectionsByContext"],
        });

        const mutationData = result.data;
        const sectionData = mutationData?.createDataVaultExtensionFieldSection?.section;

        if (!sectionData || !Object.keys(sectionData).length) {
          throw new Error("No section returned from mutation");
        }

        const newSection: Section = {
          id: sectionData.id,
          name: sectionData.name,
          description: sectionData.description || undefined,
          context: sectionData.context,
          sortOrder: sectionData.sortOrder,
          extensionFieldIds: sectionData.extensionFields?.map((field) => field.id) || [],
          fieldCount: sectionData.fieldCount || 0,
          tabName: (sectionData as unknown as { tabName?: string }).tabName || undefined,
          createdAt: sectionData.createdAt,
          modifiedAt: sectionData.modifiedAt || undefined,
        };

        return newSection;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to create section";
        throw new Error(errorMessage);
      }
    },
    [createSectionMutation],
  );

  const updateSection = useCallback(
    async (input: UpdateSectionInput): Promise<Section> => {
      try {
        const result = await updateSectionMutation({
          variables: {
            input: {
              id: input.id,
              name: input.name.trim(),
              description: input.description?.trim() || undefined,
              tabName: input.tabName?.trim() || undefined,
            },
          },

          refetchQueries: ["GetDataVaultExtensionFieldSectionsByContext"],
        });

        const mutationData = result.data;
        const sectionData = mutationData?.updateDataVaultExtensionFieldSection?.section;

        if (!sectionData || !Object.keys(sectionData).length) {
          throw new Error("No section returned from mutation");
        }

        const updatedSection: Section = {
          id: sectionData.id,
          name: sectionData.name,
          description: sectionData.description || undefined,
          context: sectionData.context,
          sortOrder: sectionData.sortOrder,
          extensionFieldIds: sectionData.extensionFields?.map((field) => field.id) || [],
          fieldCount: sectionData.fieldCount || 0,
          tabName: (sectionData as unknown as { tabName?: string }).tabName || undefined,
          createdAt: sectionData.createdAt,
          modifiedAt: sectionData.modifiedAt || undefined,
        };

        return updatedSection;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to update section";
        throw new Error(errorMessage);
      }
    },
    [updateSectionMutation],
  );

  const createGeneralSection = (ungroupedFields: CustomField[]) => {
    return {
      id: "general",
      name: "General Section",
      description: "Fields that don't belong to any specific section",
      context,
      sortOrder: 999,
      extensionFieldIds: ungroupedFields.map((field) => field.id),
      fieldCount: ungroupedFields.length,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
    };
  };

  const { sectionsWithFields, tabsWithSections } = useMemo(() => {
    if (!customFields || !sections) {
      return { sectionsWithFields: [], tabsWithSections: [] };
    }

    const fieldIdsInSections = new Set(
      sections.flatMap((section) => section.extensionFieldIds),
    );

    const sectionsWithFieldsData = sections.map((section) => {
      const sectionFields = customFields.filter((field) =>
        section.extensionFieldIds.includes(field.id),
      );

      return {
        section,
        fields: sectionFields,
      };
    });

    const ungroupedFields = customFields.filter((field) => !fieldIdsInSections.has(field.id));

    if (ungroupedFields.length > 0) {
      sectionsWithFieldsData.push({
        section: createGeneralSection(ungroupedFields),
        fields: ungroupedFields,
      });
    }

    const hasTabsEnabled = sections.some((section) => Boolean(section.tabName));

    if (hasTabsEnabled) {
      const tabGroups = new Map<string, typeof sectionsWithFieldsData>();

      sectionsWithFieldsData.forEach((sectionWithFields) => {
        const tabName = sectionWithFields.section.tabName || "Other";
        if (!tabGroups.has(tabName)) {
          tabGroups.set(tabName, []);
        }
        tabGroups.get(tabName)!.push(sectionWithFields);
      });

      const tabsWithSections = Array.from(tabGroups.entries())
        .map(([tabName, sections]) => ({ tabName, sections }))
        .sort((a, b) => {
          if (a.tabName === "Other") return 1;
          if (b.tabName === "Other") return -1;
          return a.tabName.localeCompare(b.tabName);
        });

      return {
        sectionsWithFields: sectionsWithFieldsData,
        tabsWithSections,
      };
    }

    return {
      sectionsWithFields: sectionsWithFieldsData,
      tabsWithSections: [],
    };
  }, [customFields, sections, context, createGeneralSection]);

  return {
    sections,
    loading: loading || customFieldsLoading,
    error: error || null,
    refetch,
    createSection,
    updateSection,
    sectionsWithFields,
    tabsWithSections,
    extensionFields: customFields || [],
    rawExtensionFields: rawExtensionFields || [],
  };
};
